/**
 * analytics.ts — types, slicer definitions, key-indicator definitions, and pure
 * aggregation helpers for the Distribution & Outreach page.
 *
 * The page consumes the unified distribution dataset (distribution-dataset.json,
 * produced by v1/_tools/profile-distribution.cjs) and does all slicing +
 * charting client-side. Every helper here is pure so it can be wrapped in
 * useMemo. Slicers filter MEMBERS; the kit lifecycle, the outreach rollups, the
 * combined timeline, and every key indicator are re-derived from the
 * cross-filtered member set.
 */

// ─── dataset shape ───────────────────────────────────────────────────────────

/** One de-identified member — analytic columns + an integer index only. */
export interface DistMember {
  ref: number;
  cohort: string;
  market: string;
  gender: string;
  ageBand: string;
  language: string;
  county: string;
  state: string;
  /** Full 5-digit member ZIP code. */
  zip: string;
  mso: string;
  ipa: string;
  segment: string;
  comp2024: string; // 'Y' | 'N' | ''
  comp2025: string; // 'Y' | 'N' | ''
  hasEmail: boolean;
  hasPhone: boolean;
  kitStage: string;
  kitStageIndex: number;
  // kit lifecycle milestones (ISO day strings; absent until reached)
  comingSoonLetterAt?: string;
  reminderLetterAt?: string;
  orderPlacedAt?: string;
  kitPreparedAt?: string;
  kitShippedAt?: string;
  kitDeliveredAt?: string;
  sampleShippedAt?: string;
  sampleReceivedAt?: string;
  sampleResultedAt?: string;
  resultMailedAt?: string;
  resultFaxedAt?: string;
  clinicalCallAt?: string;
  uspsStatus?: string;
  labError?: string;
  // durations (whole days; absent if not yet computable)
  daysOrderToShip?: number;
  daysShipToDeliver?: number;
  daysShipToReturn?: number;
  daysDeliverToReturn?: number;
  daysReturnToResult?: number;
  daysTotalTat?: number;
  // outreach rollups
  commsTotal?: number;
  commsFailed?: number;
  smsCount?: number;
  ivrCount?: number;
  autoCallCount?: number;
  inboundCallCount?: number;
  liveCallCount?: number;
  gotSms1?: boolean;
  gotSms2?: boolean;
  gotIvr?: boolean;
  optInOrdered?: boolean;
  firstContactAt?: string;
  lastContactAt?: string;
  lastCallResult?: string;
  /** Distinct Jason activity-code numbers (AT_n) seen in this member's log. */
  acts?: number[];
  /** Compact event stream: [dayIndex, eventTypeIndex, count]. */
  ev?: Array<[number, number, number]>;
  // ── v2 — outbound campaign + PCP letter rollups ──
  /** Number of outbound campaign calls placed to this member. */
  outboundCallCount?: number;
  /** true when any outbound call to this member had disposition starting with "Connected". */
  outboundReached?: boolean;
  /** true when any outbound call to this member was scored WARM in post-call lead-quality scoring. */
  outboundWarmLead?: boolean;
  /** true when any outbound call to this member has an MP3 attached. */
  outboundHasAudio?: boolean;
  /** Most-recent outbound disposition (text from _campaign_attributes.csv). */
  outboundLatestDisposition?: string;
  /** Most-recent outbound sentiment ("Neutral" | "Leaning Positive" | etc.). */
  outboundLatestSentiment?: string;
  /** Most-recent outbound lead-quality score ("COLD" | "WARM" | "DEAD"). */
  outboundLatestLeadScore?: string;
  /** Number of PCP fax letters on file for this member. */
  pcpLetterCount?: number;
  /** true when at least one PCP letter PDF is on file. */
  hasPcpLetter?: boolean;
  /** Most-recent PCP letter date (ISO YYYY-MM-DD). */
  pcpLatestAt?: string;
  // ── Service-activity counters (Distribution page surfaces) ──
  /** Number of "Order placed" (AT_1) events on the member's record. */
  orderCount?: number;
  /** Resamples = max(0, orderCount-1). Surfaced only when ≥1. */
  resampleCount?: number;
  /** Count of clinical follow-up events (AT_13 + AT_14). */
  clinicalCallCount?: number;
  /** Count of "Kit reported lost" (AT_17) events. */
  kitLostCount?: number;
  /** Count of "Positive result clinical call" (AT_13) events — out-of-bound (positive) FIT results. */
  outOfBoundCount?: number;
}

export interface DistCall {
  ref: number;
  day: number;
  result: string;
  language: string;
  state: string;
  /** 'live' (events table) or 'outbound' (outbound_calls table). */
  kind?: 'live' | 'outbound';
  /** Call duration in seconds when known (parsed from the source row's duration field). */
  durationSec?: number;
}

export interface DistMeta {
  datasetLabel: string;
  generatedAt: string;
  campaignStart: string;
  campaignEnd: string;
  memberCount: number;
  kitActiveCount: number;
  commsMemberCount: number;
  callCount: number;
  sources: Record<string, string>;
  deidentified: boolean;
  note: string;
}

export interface DistributionDataset {
  meta: DistMeta | null;
  dayAxis: string[];
  eventTypes: string[];
  kitStages: string[];
  activityCodes: Record<string, string>;
  members: DistMember[];
  calls: DistCall[];
}

// ─── activity codes (Jason's AT_1..AT_23 decoder, short display labels) ───────

/** Short, page-friendly labels for Jason's 23 activity codes. */
export const ACTIVITY_LABELS: Record<number, string> = {
  1: 'Order placed',
  2: 'Kit prepared',
  3: 'Kit shipped',
  4: 'Kit transit scan',
  5: 'Kit delivered',
  6: 'Sample shipped back',
  7: 'Sample transit scan',
  8: 'Sample received',
  9: 'Sample resulted',
  10: 'Result mailed to member',
  11: 'Result faxed to PCP',
  12: 'Result fax scan',
  13: 'Positive clinical call',
  14: 'Clinical call code',
  15: 'Sample failed',
  16: 'Sample failure code',
  17: 'Kit lost',
  18: 'Kit-lost reason',
  19: 'Coming-soon letter',
  20: 'Kit-shipped SMS',
  21: 'Kit-delivered SMS',
  22: 'Reminder letter',
  23: 'Sample-received SMS',
};

const actLabel = (n: number): string => ACTIVITY_LABELS[n] ?? `AT_${n}`;

// ─── slicers ─────────────────────────────────────────────────────────────────

export interface SlicerDef {
  key: string;
  label: string;
  group: string;
  /** Single-value dimension — one value per member. */
  accessor?: (m: DistMember) => string;
  /** Multi-value dimension — a member can carry several values (e.g. Activity). */
  multi?: (m: DistMember) => string[];
  /** High-cardinality dimensions render a search box + capped list. */
  searchable?: boolean;
}

const compLabel = (v: string): string =>
  v === 'Y' ? 'Compliant' : v === 'N' ? 'Non-compliant' : 'No record';

/**
 * The full slicer set — 19 slicers in 7 groups. The first five groups mirror
 * the Members & Eligibility page (page 02) one-for-one; the last two add the
 * kit-lifecycle dimensions and an Activity slicer carrying every Jason code.
 * Every slicer cross-filters all three page modes and every key indicator.
 */
export const SLICERS: SlicerDef[] = [
  // ── Geography ──
  { key: 'state', label: 'State', group: 'Geography', accessor: (m) => m.state },
  { key: 'county', label: 'County', group: 'Geography', accessor: (m) => m.county, searchable: true },
  { key: 'zip', label: 'ZIP', group: 'Geography', accessor: (m) => m.zip, searchable: true },
  { key: 'market', label: 'Market', group: 'Geography', accessor: (m) => m.market },
  // ── Demographics ──
  { key: 'gender', label: 'Gender', group: 'Demographics', accessor: (m) => m.gender },
  { key: 'ageBand', label: 'Age band', group: 'Demographics', accessor: (m) => m.ageBand },
  { key: 'language', label: 'Language', group: 'Demographics', accessor: (m) => m.language, searchable: true },
  // ── Program ──
  { key: 'cohort', label: 'Cohort', group: 'Program', accessor: (m) => m.cohort },
  { key: 'mso', label: 'MSO', group: 'Program', accessor: (m) => m.mso, searchable: true },
  { key: 'ipa', label: 'IPA', group: 'Program', accessor: (m) => m.ipa, searchable: true },
  // ── Screening history ──
  { key: 'segment', label: 'Screening segment', group: 'Screening history', accessor: (m) => m.segment },
  { key: 'comp2025', label: '2025 status', group: 'Screening history', accessor: (m) => compLabel(m.comp2025) },
  { key: 'comp2024', label: '2024 status', group: 'Screening history', accessor: (m) => compLabel(m.comp2024) },
  // ── Reachability ──
  { key: 'email', label: 'Has email', group: 'Reachability', accessor: (m) => (m.hasEmail ? 'Has email' : 'No email') },
  { key: 'phone', label: 'Has phone', group: 'Reachability', accessor: (m) => (m.hasPhone ? 'Has phone' : 'No phone') },
  // ── Kit & outreach ──
  { key: 'kitStage', label: 'Kit stage', group: 'Kit & outreach', accessor: (m) => m.kitStage },
  { key: 'usps', label: 'Delivery status', group: 'Kit & outreach', accessor: (m) => m.uspsStatus || 'No scan yet' },
  {
    key: 'outreach',
    label: 'Outreach status',
    group: 'Kit & outreach',
    accessor: (m) => ((m.commsTotal ?? 0) + (m.liveCallCount ?? 0) > 0 ? 'Contacted' : 'Not contacted'),
  },
  // ── Action — every Jason activity code, multi-value ──
  {
    key: 'activity',
    label: 'Activity',
    group: 'Action',
    searchable: true,
    multi: (m) => (m.acts ?? []).map(actLabel),
  },
  // ── Outbound campaign (v2) ──
  {
    key: 'outboundCalled',
    label: 'Outbound called',
    group: 'Outbound campaign',
    accessor: (m) => ((m.outboundCallCount ?? 0) > 0 ? 'Called' : 'Not called'),
  },
  {
    key: 'outboundReached',
    label: 'Outbound reached',
    group: 'Outbound campaign',
    accessor: (m) =>
      (m.outboundCallCount ?? 0) === 0
        ? 'No call'
        : m.outboundReached
          ? 'Reached'
          : 'Not reached',
  },
  {
    key: 'outboundLeadScore',
    label: 'Outbound lead score',
    group: 'Outbound campaign',
    accessor: (m) => m.outboundLatestLeadScore || 'No score',
  },
  {
    key: 'outboundSentiment',
    label: 'Outbound sentiment',
    group: 'Outbound campaign',
    accessor: (m) => m.outboundLatestSentiment || 'No score',
  },
  // ── PCP letter (v2) ──
  {
    key: 'pcpLetter',
    label: 'PCP letter on file',
    group: 'Outbound campaign',
    accessor: (m) => (m.hasPcpLetter ? 'Has PCP letter' : 'No PCP letter'),
  },
];

export const SLICER_GROUPS = [
  'Geography',
  'Demographics',
  'Program',
  'Screening history',
  'Reachability',
  'Kit & outreach',
  'Action',
  'Outbound campaign',
];

/** filters[slicerKey] = selected values; empty / absent means "no filter". */
export type Filters = Record<string, string[]>;

/** AND across slicers, OR within a slicer. Multi-value slicers match on overlap. */
export function applyFilters(members: DistMember[], filters: Filters): DistMember[] {
  const active = SLICERS.filter((s) => (filters[s.key]?.length ?? 0) > 0);
  if (active.length === 0) return members;
  return members.filter((m) =>
    active.every((s) => {
      const sel = filters[s.key]!;
      if (s.multi) return s.multi(m).some((v) => sel.includes(v));
      return sel.includes(s.accessor!(m));
    }),
  );
}

// ─── time-range windowing ────────────────────────────────────────────────────

/** Inclusive date window. Either bound may be omitted for an open-ended side. */
export interface TimeWindow {
  start?: string | undefined;
  end?: string | undefined;
}

const inRange = (iso: string | undefined, start: string | undefined, end: string | undefined): boolean => {
  if (!iso) return false;
  const d = iso.slice(0, 10);
  if (start && d < start) return false;
  if (end && d > end) return false;
  return true;
};

/** True when the window is unset (or fully open). */
export function isWindowOpen(w: TimeWindow | null | undefined): boolean {
  return !w || (!w.start && !w.end);
}

/**
 * Re-derive each member's event-based rollups against an inclusive [start, end]
 * date window. Members whose kit-lifecycle milestones and per-day events all
 * fall outside the window are dropped. For surviving members, kit-stage,
 * communications counts, and "got SMS / IVR" flags reflect the in-window
 * activity only. Outbound/PCP rollups + Jason activity codes (acts[]) stay
 * whole-history because they are not date-stamped at the member-row level.
 */
export function applyTimeWindow(
  members: DistMember[],
  window: TimeWindow | null | undefined,
  dayAxis: string[],
  eventTypes: string[],
): DistMember[] {
  if (isWindowOpen(window)) return members;
  const start = window?.start || undefined;
  const end = window?.end || undefined;

  // Map dayAxis indices to ISO date strings (already in the axis).
  // The window contains an event when dayAxis[idx] is in [start, end].
  const dayInWindow: boolean[] = dayAxis.map((iso) => inRange(iso, start, end));

  // Map each eventType name to its rollup bucket so we can replay counts.
  const COMM_BUCKETS: Record<string, 'sms' | 'ivr' | 'autoCall' | 'inboundCall' | 'liveCall'> = {
    sms: 'sms',
    ivr: 'ivr',
    autoCall: 'autoCall',
    inboundCall: 'inboundCall',
    liveCall: 'liveCall',
  };
  const typeBucket: Array<keyof typeof COMM_BUCKETS | null> = eventTypes.map((t) =>
    Object.prototype.hasOwnProperty.call(COMM_BUCKETS, t) ? (t as keyof typeof COMM_BUCKETS) : null,
  );

  // Milestone date list per stage (stages indexed by kitStages[]).
  const stageDate = (m: DistMember, i: number): string | undefined => {
    switch (i) {
      case 0: return undefined; // stage 0 = targeted (always reached)
      case 1: return m.orderPlacedAt;
      case 2: return m.kitPreparedAt;
      case 3: return m.kitShippedAt;
      case 4: return m.kitDeliveredAt;
      case 5: return m.sampleReceivedAt || m.sampleShippedAt;
      case 6: return m.sampleResultedAt;
      default: return undefined;
    }
  };

  const out: DistMember[] = [];
  for (const m of members) {
    // Re-derive kitStageIndex from milestone dates in window.
    let windowedStage = 0;
    for (let i = 6; i >= 1; i--) {
      const d = stageDate(m, i);
      if (d && inRange(d, start, end)) { windowedStage = i; break; }
    }

    // Replay m.ev[] against the day window to derive comm counts + flags.
    let smsCount = 0, ivrCount = 0, autoCallCount = 0, inboundCallCount = 0, liveCallCount = 0;
    let smsTouches = 0;
    let gotIvr = false;
    if (m.ev) {
      for (const [d, t, c] of m.ev) {
        if (!dayInWindow[d]) continue;
        const bucket = typeBucket[t];
        if (!bucket) continue;
        if (bucket === 'sms') { smsCount += c; smsTouches += c; }
        else if (bucket === 'ivr') { ivrCount += c; gotIvr = true; }
        else if (bucket === 'autoCall') { autoCallCount += c; gotIvr = true; }
        else if (bucket === 'inboundCall') inboundCallCount += c;
        else if (bucket === 'liveCall') liveCallCount += c;
      }
    }
    const commsTotal = smsCount + ivrCount + autoCallCount + inboundCallCount + liveCallCount;
    const commsFailed = 0; // failures are not in ev[] today; safe lower bound

    // Drop members with no in-window activity at all.
    if (windowedStage === 0 && commsTotal === 0) continue;

    out.push({
      ...m,
      kitStageIndex: windowedStage,
      kitStage: windowedStage > 0 ? m.kitStage : 'Targeted',
      smsCount,
      ivrCount,
      autoCallCount,
      inboundCallCount,
      liveCallCount,
      commsTotal,
      commsFailed,
      gotSms1: smsTouches >= 1,
      gotSms2: smsTouches >= 2,
      gotIvr,
    });
  }
  return out;
}

export function activeFilterCount(filters: Filters): number {
  return SLICERS.reduce((n, s) => n + ((filters[s.key]?.length ?? 0) > 0 ? 1 : 0), 0);
}

/** Distinct values + counts for one slicer over a member set (multi-aware). */
export function slicerTally(members: DistMember[], s: SlicerDef): Map<string, number> {
  const m = new Map<string, number>();
  for (const r of members) {
    if (s.multi) {
      for (const v of s.multi(r)) m.set(v, (m.get(v) ?? 0) + 1);
    } else {
      const k = s.accessor!(r);
      m.set(k, (m.get(k) ?? 0) + 1);
    }
  }
  return m;
}

// ─── generic tally ───────────────────────────────────────────────────────────

export interface Tally {
  label: string;
  value: number;
}

/** Sorted-descending count of accessor values over items. */
export function tally<T>(items: T[], accessor: (x: T) => string): Tally[] {
  const m = new Map<string, number>();
  for (const it of items) {
    const k = accessor(it);
    m.set(k, (m.get(k) ?? 0) + 1);
  }
  return [...m.entries()].map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value);
}

export function topN(t: Tally[], n: number): Tally[] {
  return t.slice(0, n);
}

// ─── kit lifecycle funnel ────────────────────────────────────────────────────

export interface FunnelStep {
  label: string;
  value: number;
  pct: number; // percent of the funnel's top step
}

/** Cumulative funnel — members at or beyond each kit stage. */
export function kitFunnel(members: DistMember[], kitStages: string[]): FunnelStep[] {
  const top = members.length;
  return kitStages.map((label, i) => {
    const value = members.reduce((n, m) => n + (m.kitStageIndex >= i ? 1 : 0), 0);
    return { label, value, pct: top > 0 ? Math.round((value / top) * 1000) / 10 : 0 };
  });
}

/** Opt-In conversion funnel — outreach steps for the opt-in cohort. */
export function optInFunnel(members: DistMember[]): FunnelStep[] {
  const optIn = members.filter((m) => m.cohort === 'Opt-In');
  const n = optIn.length;
  const steps: Tally[] = [
    { label: 'Opt-In targeted', value: n },
    { label: 'Received SMS #1', value: optIn.filter((m) => m.gotSms1).length },
    { label: 'Received SMS #2', value: optIn.filter((m) => m.gotSms2).length },
    { label: 'Received IVR', value: optIn.filter((m) => m.gotIvr).length },
    { label: 'Kit ordered', value: optIn.filter((m) => !!m.orderPlacedAt).length },
  ];
  return steps.map((s) => ({ ...s, pct: n > 0 ? Math.round((s.value / n) * 1000) / 10 : 0 }));
}

// ─── combined event timeline ─────────────────────────────────────────────────

/** Event types collapse into 5 timeline series for a legible stacked chart. */
const CATEGORY_OF: Record<string, string> = {
  comingSoonLetter: 'Letters',
  reminderLetter: 'Letters',
  orderPlaced: 'Kit events',
  kitPrepared: 'Kit events',
  kitShipped: 'Kit events',
  kitDelivered: 'Kit events',
  sampleShipped: 'Kit events',
  sampleReceived: 'Kit events',
  sampleResulted: 'Kit events',
  resultMailed: 'Kit events',
  resultFaxed: 'Kit events',
  clinicalCall: 'Kit events',
  sms: 'SMS',
  ivr: 'IVR / auto-call',
  autoCall: 'IVR / auto-call',
  inboundCall: 'Calls',
  liveCall: 'Calls',
};

export const TIMELINE_SERIES = ['Letters', 'Kit events', 'SMS', 'IVR / auto-call', 'Calls'];

export type TimelineRow = Record<string, string | number>;

/** Per-day stacked counts across the cross-filtered member set. */
export function timelineRows(
  members: DistMember[],
  dayAxis: string[],
  eventTypes: string[],
): TimelineRow[] {
  const catByIdx = eventTypes.map((t) => CATEGORY_OF[t] ?? 'Kit events');
  const grid: Record<string, number>[] = dayAxis.map(() => ({
    Letters: 0,
    'Kit events': 0,
    SMS: 0,
    'IVR / auto-call': 0,
    Calls: 0,
  }));
  for (const m of members) {
    if (!m.ev) continue;
    for (const [d, t, c] of m.ev) {
      const cell = grid[d];
      const cat = catByIdx[t];
      if (cell && cat) cell[cat] = (cell[cat] ?? 0) + c;
    }
  }
  return dayAxis.map((day, i) => ({ day: day.slice(5), ...(grid[i] ?? {}) }));
}

// ─── outreach channel mix ────────────────────────────────────────────────────

export function channelMix(members: DistMember[]): Tally[] {
  let sms = 0, ivr = 0, auto = 0, inbound = 0, live = 0;
  for (const m of members) {
    sms += m.smsCount ?? 0;
    ivr += m.ivrCount ?? 0;
    auto += m.autoCallCount ?? 0;
    inbound += m.inboundCallCount ?? 0;
    live += m.liveCallCount ?? 0;
  }
  return [
    { label: 'SMS', value: sms },
    { label: 'IVR', value: ivr },
    { label: 'Automated call', value: auto },
    { label: 'Live call', value: live },
    { label: 'Inbound call', value: inbound },
  ];
}

// ─── turnaround durations ────────────────────────────────────────────────────

/** percentile of a pre-sorted ascending array. */
export function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  const i = Math.min(sorted.length - 1, Math.max(0, Math.ceil((p / 100) * sorted.length) - 1));
  return sorted[i] ?? 0;
}

/** Duration distribution as day-buckets — for a histogram bar chart. */
export function durationHistogram(get: (m: DistMember) => number | undefined, members: DistMember[]): Tally[] {
  const buckets: Array<{ label: string; lo: number; hi: number }> = [
    { label: 'Same day', lo: 0, hi: 0 },
    { label: '1 day', lo: 1, hi: 1 },
    { label: '2-3 d', lo: 2, hi: 3 },
    { label: '4-7 d', lo: 4, hi: 7 },
    { label: '8-14 d', lo: 8, hi: 14 },
    { label: '15+ d', lo: 15, hi: Infinity },
  ];
  const counts = buckets.map(() => 0);
  for (const m of members) {
    const v = get(m);
    if (typeof v !== 'number') continue;
    const bi = buckets.findIndex((b) => v >= b.lo && v <= b.hi);
    if (bi >= 0) counts[bi] = (counts[bi] ?? 0) + 1;
  }
  return buckets.map((b, i) => ({ label: b.label, value: counts[i] ?? 0 }));
}

/** Median of a duration, grouped by an arbitrary dimension — for a submarket chart. */
export function medianDurationByGroup(
  members: DistMember[],
  get: (m: DistMember) => number | undefined,
  group: (m: DistMember) => string,
  limit = 12,
): Tally[] {
  const buckets = new Map<string, number[]>();
  for (const m of members) {
    const v = get(m);
    if (typeof v !== 'number') continue;
    const g = group(m);
    if (!buckets.has(g)) buckets.set(g, []);
    buckets.get(g)!.push(v);
  }
  const rows: Array<{ label: string; value: number; n: number }> = [];
  for (const [label, vals] of buckets) {
    vals.sort((a, b) => a - b);
    rows.push({ label, value: percentile(vals, 50), n: vals.length });
  }
  return rows
    .sort((a, b) => b.n - a.n)
    .slice(0, limit)
    .map((r) => ({ label: r.label, value: r.value }));
}

// ─── key indicators (flip-card metrics) ──────────────────────────────────────

/**
 * Every "key indicator" the page surfaces as a flip card: the front shows the
 * computed value, the back explains the formula and the industry context.
 * Each indicator recomputes against the cross-filtered member set, so a slicer
 * selection (down to a single ZIP submarket) re-derives every value.
 */
export type IndicatorUnit = 'percent' | 'days' | 'ratio';

export interface IndicatorResult {
  /** Formatted headline value, e.g. "23.8%", "4 d", "3.4". */
  display: string;
  /** Denominator / sample size behind the value. */
  n: number;
  /** Short note describing the basis (e.g. "p90 9 d · n=1,204"). */
  basis: string;
  /** Numeric value, or null when not yet computable. */
  raw: number | null;
}

export interface IndicatorDef {
  key: string;
  label: string;
  group: string;
  unit: IndicatorUnit;
  /** Human-readable formula, shown on the card back. */
  formula: string;
  /** What it measures + industry grounding, shown on the card back. */
  about: string;
  compute: (members: DistMember[]) => IndicatorResult;
}

export const INDICATOR_GROUPS = ['Conversion & yield', 'Turnaround', 'Outreach', 'Outbound campaign'];

// indicator compute helpers
const atStage = (ms: DistMember[], i: number): number =>
  ms.reduce((n, m) => n + (m.kitStageIndex >= i ? 1 : 0), 0);
const hasAct = (m: DistMember, code: number): boolean => (m.acts ?? []).includes(code);
const countAct = (ms: DistMember[], code: number): number =>
  ms.reduce((n, m) => n + (hasAct(m, code) ? 1 : 0), 0);
const sumOf = (ms: DistMember[], get: (m: DistMember) => number | undefined): number =>
  ms.reduce((s, m) => s + (get(m) ?? 0), 0);

function rateResult(num: number, den: number, basisNoun: string): IndicatorResult {
  if (den <= 0) return { display: '—', n: 0, basis: `awaiting ${basisNoun}`, raw: null };
  const r = (num / den) * 100;
  return {
    display: `${(Math.round(r * 10) / 10).toFixed(1)}%`,
    n: den,
    basis: `${num.toLocaleString()} of ${den.toLocaleString()} ${basisNoun}`,
    raw: r,
  };
}

function durResult(members: DistMember[], get: (m: DistMember) => number | undefined): IndicatorResult {
  const v: number[] = [];
  for (const m of members) {
    const x = get(m);
    if (typeof x === 'number') v.push(x);
  }
  v.sort((a, b) => a - b);
  if (v.length === 0) return { display: '—', n: 0, basis: 'awaiting returned kits', raw: null };
  const p50 = percentile(v, 50);
  return {
    display: `${p50} d`,
    n: v.length,
    basis: `p90 ${percentile(v, 90)} d · mean ${(v.reduce((s, x) => s + x, 0) / v.length).toFixed(1)} d · n=${v.length.toLocaleString()}`,
    raw: p50,
  };
}

export const INDICATORS: IndicatorDef[] = [
  // ── Conversion & yield ──
  {
    key: 'orderRate',
    label: 'Kit order rate',
    group: 'Conversion & yield',
    unit: 'percent',
    formula: 'Kits ordered ÷ Members targeted × 100',
    about:
      'Share of the targeted population with a kit order on file — the first conversion step of a mailed-FIT campaign. Auto-Send members convert here automatically; Opt-In members must respond to outreach first.',
    compute: (m) => rateResult(atStage(m, 1), m.length, 'members targeted'),
  },
  {
    key: 'fulfillmentRate',
    label: 'Fulfillment rate',
    group: 'Conversion & yield',
    unit: 'percent',
    formula: 'Kits shipped ÷ Kits ordered × 100',
    about:
      'How reliably an order becomes a kit in the mail stream. A healthy pipeline runs close to 100%; a sustained gap signals a prep or shipping backlog.',
    compute: (m) => rateResult(atStage(m, 3), atStage(m, 1), 'kits ordered'),
  },
  {
    key: 'deliveryRate',
    label: 'Delivery success rate',
    group: 'Conversion & yield',
    unit: 'percent',
    formula: 'Kits delivered ÷ Kits shipped × 100',
    about:
      'USPS delivery-confirmation rate. Kits that never confirm delivery point to roster address quality — undeliverable-as-addressed, vacant, or moved-no-forwarding scans.',
    compute: (m) => rateResult(atStage(m, 4), atStage(m, 3), 'kits shipped'),
  },
  {
    key: 'returnRate',
    label: 'Kit return rate',
    group: 'Conversion & yield',
    unit: 'percent',
    formula: 'Samples returned ÷ Kits delivered × 100',
    about:
      'The headline FIT-program metric — delivered kits the member mails back.',
    compute: (m) => rateResult(atStage(m, 5), atStage(m, 4), 'kits delivered'),
  },
  {
    key: 'validSampleRate',
    label: 'Valid sample rate',
    group: 'Conversion & yield',
    unit: 'percent',
    formula: 'Samples resulted ÷ Samples returned × 100',
    about:
      'Share of returned kits the lab could actually result. The complement is the lab-failure rate — insufficient sample, expired collection device, or an unsigned requisition.',
    compute: (m) => rateResult(atStage(m, 6), atStage(m, 5), 'samples returned'),
  },
  {
    key: 'labFailureRate',
    label: 'Lab failure rate',
    group: 'Conversion & yield',
    unit: 'percent',
    formula: 'Samples failed ÷ Samples returned × 100',
    about:
      'Returned kits the lab could not process. Common causes: insufficient sample volume, wrong or expired device, hemolysis, or a missing collection date.',
    compute: (m) => {
      const failed = m.filter((x) => hasAct(x, 15) || !!x.labError).length;
      return rateResult(failed, atStage(m, 5), 'samples returned');
    },
  },
  {
    key: 'positivityRate',
    label: 'FIT positivity rate',
    group: 'Conversion & yield',
    unit: 'percent',
    formula: 'Positive results ÷ Samples resulted × 100',
    about:
      'Share of resulted tests flagged positive (blood detected) and routed to a clinical follow-up call.',
    compute: (m) => rateResult(countAct(m, 13), atStage(m, 6), 'samples resulted'),
  },
  {
    key: 'kitLossRate',
    label: 'Kit loss rate',
    group: 'Conversion & yield',
    unit: 'percent',
    formula: 'Kits lost ÷ Kits shipped × 100',
    about:
      'Shipped kits flagged lost by the member or the carrier — pipeline leakage that needs a re-ship.',
    compute: (m) => rateResult(countAct(m, 17), atStage(m, 3), 'kits shipped'),
  },
  // ── Turnaround ──
  {
    key: 'tatOrderShip',
    label: 'Order → ship',
    group: 'Turnaround',
    unit: 'days',
    formula: 'median( Kit shipped date − Order placed date )',
    about:
      'Internal fulfillment speed — whole days from an order being placed to the kit entering the mail. The one turnaround leg fully under the program’s control.',
    compute: (m) => durResult(m, (x) => x.daysOrderToShip),
  },
  {
    key: 'tatShipDeliver',
    label: 'Ship → delivery',
    group: 'Turnaround',
    unit: 'days',
    formula: 'median( Kit delivered date − Kit shipped date )',
    about:
      'USPS transit time from the ship scan to the delivery scan. Drives how soon the member-side return clock can even start.',
    compute: (m) => durResult(m, (x) => x.daysShipToDeliver),
  },
  {
    key: 'tatShipReturn',
    label: 'Ship → sample return',
    group: 'Turnaround',
    unit: 'days',
    formula: 'median( Sample received date − Kit shipped date )',
    about:
      'Time from shipment to the lab receiving the member’s sample — the full member-side turnaround. Most members who return a FIT do so within roughly two weeks. Slice by ZIP, county, or market to compare submarkets.',
    compute: (m) => durResult(m, (x) => x.daysShipToReturn),
  },
  {
    key: 'tatMemberDwell',
    label: 'Member dwell time',
    group: 'Turnaround',
    unit: 'days',
    formula: 'median( Sample received date − Kit delivered date )',
    about:
      'How long a kit sits with the member between delivery and mail-back. This is the behavior window reminder outreach is designed to compress.',
    compute: (m) => durResult(m, (x) => x.daysDeliverToReturn),
  },
  {
    key: 'tatLab',
    label: 'Lab turnaround',
    group: 'Turnaround',
    unit: 'days',
    formula: 'median( Sample resulted date − Sample received date )',
    about:
      'Laboratory turnaround time — specimen received to result reported, the standard clinical-lab TAT definition.',
    compute: (m) => durResult(m, (x) => x.daysReturnToResult),
  },
  {
    key: 'tatTotal',
    label: 'Total turnaround',
    group: 'Turnaround',
    unit: 'days',
    formula: 'median( Sample resulted date − Order placed date )',
    about:
      'End-to-end campaign cycle time for a completed screen — order placed to result reported, spanning fulfillment, transit, member dwell, and lab processing.',
    compute: (m) => durResult(m, (x) => x.daysTotalTat),
  },
  // ── Outreach ──
  {
    key: 'reachRate',
    label: 'Outreach reach rate',
    group: 'Outreach',
    unit: 'percent',
    formula: 'Members contacted (≥1 SMS / IVR / call) ÷ Members targeted × 100',
    about:
      'Share of the targeted population reached on at least one channel. Reach is the ceiling on outreach-driven conversion.',
    compute: (m) => {
      const contacted = m.filter((x) => (x.commsTotal ?? 0) + (x.liveCallCount ?? 0) > 0).length;
      return rateResult(contacted, m.length, 'members targeted');
    },
  },
  {
    key: 'msgPerMember',
    label: 'Messages per member',
    group: 'Outreach',
    unit: 'ratio',
    formula: 'Total outbound + inbound messages ÷ Members targeted',
    about:
      'Outreach intensity. SMS-led cadences typically run several touches per member across the campaign window; a low value early means the cadence has not yet fired.',
    compute: (m) => {
      if (m.length === 0) return { display: '—', n: 0, basis: 'no members', raw: null };
      const total = sumOf(m, (x) => x.commsTotal);
      const v = total / m.length;
      return {
        display: (Math.round(v * 10) / 10).toFixed(1),
        n: m.length,
        basis: `${total.toLocaleString()} messages ÷ ${m.length.toLocaleString()} members`,
        raw: v,
      };
    },
  },
  {
    key: 'optInConversion',
    label: 'Opt-In conversion rate',
    group: 'Outreach',
    unit: 'percent',
    formula: 'Opt-In members who ordered ÷ Opt-In members targeted × 100',
    about:
      'For the Opt-In cohort, the share that responded to outreach and placed a kit order — the core funnel for opt-in campaigns, where no kit ships until the member acts.',
    compute: (m) => {
      const optIn = m.filter((x) => x.cohort === 'Opt-In');
      const ordered = optIn.filter((x) => x.kitStageIndex >= 1).length;
      return rateResult(ordered, optIn.length, 'Opt-In members');
    },
  },
  {
    key: 'messageDeliveryRate',
    label: 'Message delivery rate',
    group: 'Outreach',
    unit: 'percent',
    formula: '(Messages sent − Failed messages) ÷ Messages sent × 100',
    about:
      'Share of outreach messages that did not fail at the carrier. Persistent failures usually mean a stale or invalid phone number on the roster.',
    compute: (m) => {
      const total = sumOf(m, (x) => x.commsTotal);
      const failed = sumOf(m, (x) => x.commsFailed);
      return rateResult(total - failed, total, 'messages sent');
    },
  },
  // ── Outbound campaign (v2) ─────────────────────────────────────────────
  {
    key: 'outboundCallRate',
    label: 'Outbound call coverage',
    group: 'Outbound campaign',
    unit: 'percent',
    formula: 'Members called ÷ Members targeted × 100',
    about:
      'Share of the cross-filtered member set who received at least one outbound campaign call. A live-agent reach metric — independent of automated SMS / IVR / mail outreach.',
    compute: (m) => {
      const called = m.filter((x) => (x.outboundCallCount ?? 0) > 0).length;
      return rateResult(called, m.length, 'members targeted');
    },
  },
  {
    key: 'outboundReachedRate',
    label: 'Outbound connect rate',
    group: 'Outbound campaign',
    unit: 'percent',
    formula: 'Members whose outbound call connected ÷ Members called × 100',
    about:
      'Of members the campaign actually called, the share where the disposition was "Connected" (engaged or not interested). The remainder went to voicemail, no answer, or wrong number.',
    compute: (m) => {
      const called = m.filter((x) => (x.outboundCallCount ?? 0) > 0);
      const reached = called.filter((x) => x.outboundReached).length;
      return rateResult(reached, called.length, 'members called');
    },
  },
  {
    key: 'outboundWarmRate',
    label: 'Warm-lead rate',
    group: 'Outbound campaign',
    unit: 'percent',
    formula: 'Members scored WARM ÷ Members called × 100',
    about:
      'Of members the campaign called, the share scored WARM in post-call lead-quality scoring (engaged, low objection, moderate-to-high interest). WARM ≈ deserves follow-up; COLD ≈ unlikely to convert on this attempt.',
    compute: (m) => {
      const called = m.filter((x) => (x.outboundCallCount ?? 0) > 0);
      const warm = called.filter((x) => x.outboundWarmLead).length;
      return rateResult(warm, called.length, 'members called');
    },
  },
  {
    key: 'pcpLetterCoverage',
    label: 'PCP letter coverage',
    group: 'Outbound campaign',
    unit: 'percent',
    formula: 'Members with a PCP fax letter on file ÷ Members targeted × 100',
    about:
      'Share of the cross-filtered population for whom a PCP coordination letter has been faxed to the PCP and the PDF is on file in the campaign letter library. The PCP letter is the clinical handoff artifact for positive results and follow-up coordination.',
    compute: (m) => {
      const covered = m.filter((x) => x.hasPcpLetter).length;
      return rateResult(covered, m.length, 'members targeted');
    },
  },
];

// ─── per-indicator-group hero KPI + supporting tiles ─────────────────────────

/**
 * For the glass-card / dashboard-row layouts, each indicator group surfaces one
 * hero KPI (the ring gauge) and a small set of supporting KPI tiles. The hero
 * is the indicator most users glance at first; the supports add context without
 * cluttering the hero.
 */
export interface GroupLayout {
  group: string;
  hero: string; // INDICATORS[].key
  tiles: string[]; // INDICATORS[].key
  /** Description shown under the group title. Plain English, < 60 chars. */
  blurb: string;
}

export const GROUP_LAYOUTS: GroupLayout[] = [
  {
    group: 'Conversion & yield',
    hero: 'returnRate',
    tiles: ['orderRate', 'validSampleRate'],
    blurb: 'Order → ship → deliver → return → result conversion.',
  },
  {
    group: 'Turnaround',
    hero: 'tatShipReturn',
    tiles: ['tatOrderShip', 'tatLab', 'tatTotal'],
    blurb: 'Days between milestones — fulfillment, transit, lab.',
  },
  {
    group: 'Outreach',
    hero: 'reachRate',
    tiles: ['msgPerMember', 'optInConversion', 'messageDeliveryRate'],
    blurb: 'SMS / IVR / live-call cadence reach + delivery.',
  },
  {
    group: 'Outbound campaign',
    hero: 'outboundWarmRate',
    tiles: ['outboundCallRate', 'outboundReachedRate', 'pcpLetterCoverage'],
    blurb: 'Live-agent dial coverage + warm-lead rate + PCP letter.',
  },
];

// ─── sparkline (per-day series for the headline KPI of a group) ──────────────

/**
 * Per-day numerator-bucket series so a card can render a sparkline that
 * matches its hero metric. Each value is the count of relevant events on
 * dayAxis[i]; the consumer normalises however it wants. No denominator
 * smoothing here — sparklines are deliberately coarse, just a shape.
 */
export function sparklineSeries(
  members: DistMember[],
  hero: string,
  dayAxis: string[],
  eventTypes: string[],
): number[] {
  const grid = new Array<number>(dayAxis.length).fill(0);
  if (members.length === 0 || dayAxis.length === 0) return grid;

  // Map eventType name → which type contributes for a given hero KPI.
  const typeOf = new Map<string, number>();
  for (let i = 0; i < eventTypes.length; i++) typeOf.set(eventTypes[i] as string, i);
  const pick = (name: string): number => typeOf.get(name) ?? -1;

  // Pick the eventTypes that the hero KPI counts on its numerator side.
  let interesting: number[];
  switch (hero) {
    case 'orderRate':
      interesting = [pick('orderPlaced')]; break;
    case 'fulfillmentRate':
      interesting = [pick('kitShipped')]; break;
    case 'deliveryRate':
      interesting = [pick('kitDelivered')]; break;
    case 'returnRate':
      interesting = [pick('sampleReceived'), pick('sampleShipped')]; break;
    case 'validSampleRate':
      interesting = [pick('sampleResulted')]; break;
    case 'tatOrderShip':
    case 'tatShipDeliver':
    case 'tatShipReturn':
    case 'tatMemberDwell':
    case 'tatLab':
    case 'tatTotal':
      interesting = [pick('kitShipped'), pick('sampleReceived'), pick('sampleResulted')]; break;
    case 'reachRate':
    case 'msgPerMember':
    case 'messageDeliveryRate':
      interesting = [pick('sms'), pick('ivr'), pick('autoCall'), pick('liveCall'), pick('inboundCall')]; break;
    case 'optInConversion':
      interesting = [pick('sms'), pick('ivr'), pick('orderPlaced')]; break;
    case 'outboundWarmRate':
    case 'outboundCallRate':
    case 'outboundReachedRate':
      interesting = [pick('liveCall')]; break;
    case 'pcpLetterCoverage':
      interesting = [pick('resultFaxed')]; break;
    default:
      interesting = [];
  }
  const want = new Set(interesting.filter((i) => i >= 0));
  if (want.size === 0) return grid;

  for (const m of members) {
    if (!m.ev) continue;
    for (const [d, t, c] of m.ev) {
      if (want.has(t) && d >= 0 && d < grid.length) grid[d] = (grid[d] ?? 0) + c;
    }
  }
  return grid;
}
